import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Link, Typography } from "@mui/material";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import ScreenshotTwoColumnGallery from "../../mobile-sliding/components/ScreenshotTwoColumnGallery";
import ScreenshotBoard from "../board/ScreenshotBoard";
import { useBoardMatColor } from "../board/useBoardMatColor";
import ScreenshotExpiredPage from "../components/ScreenshotExpiredPage";
import JigsawShowcaseGoogleAdCreative from "../components/JigsawShowcaseGoogleAdLayouts";
import {
    DailyLiveOpsPanel,
    DailyParticleField,
    JourneyMapLayout,
    ToolsUtilityBar,
} from "../components/ScreenshotMarketingPanels";
import ScreenshotPhoneFrame from "../components/ScreenshotPhoneFrame";
import ScreenshotPiecesDifficultyBar from "../components/ScreenshotPiecesDifficultyBar";
import ScreenshotPiecesTray from "../components/ScreenshotPiecesTray";
import ScreenshotPromoHeader from "../components/ScreenshotPromoHeader";
import ScreenshotPuzzleTitleBar from "../components/ScreenshotPuzzleTitleBar";
import ColorBasketScreenshotLayout from "../components/ColorBasketScreenshotLayout";
import SortingAsymLayout from "../components/SortingAsymLayout";
import VaultPackMenu from "../components/VaultPackMenu";
import ScreenshotDesignFrame from "../../../components/ScreenshotDesignFrame";
import {
    screenshotSceneKey,
    useScreenshotTranslation,
} from "../../../lib/screenshotI18n";
import { screenshotFontStack, screenshotShellFontSx } from "../../../lib/screenshotFonts";
import { markScreenshotNotReady, signalScreenshotSceneReady } from "../../../lib/screenshotReady";
import { loadColorBasketsForJigsaw } from "../../../pages/puzzles/jigsaw/JigsawColorBasketPlayService";
import {
    getImageUrl,
    getStoryTitle,
    getTemplateGrid,
    loadScreenshotGallery,
    loadScreenshotJigsaw,
} from "../lib/api";
import { isScreenshotSceneExpired } from "../lib/screenshotAccess";
import { computeScreenshotPieceLayout } from "../lib/screenshotPieceLayout";
import {
    getScreenshotScene,
    isColorBasketDemoScene,
    isJourneyMapScene,
    isScreenshotSceneConfigured,
    isTwoColumnGalleryScene,
    isVaultMenuScene,
} from "../screenshotScenes";
import { MAIN_PATH } from "../routes";
import ScreenshotApplePlayPage from "./ScreenshotApplePlayPage";
import {
    SCREENSHOT_DEVICES,
    resolveScreenshotDevice,
} from "../../../components/ScreenshotDevicePicker";
import { trayRegionSx } from "../styles/mainStyles";
import {
    boardFrameSx,
    boardRegionFramedSx,
    boardRegionSx,
    dailyShellSx,
    galleryPhoneShellSx,
    giantShellSx,
    glowingBoardWrapSx,
    heroShellSx,
    jigsawScreenshotAccent,
    jigsawShellBgForScene,
    phoneShellSx,
    toolsShellSx,
} from "../styles/screenshotStyles";

function pickHighlightBasketIndex(baskets) {
    if (!Array.isArray(baskets) || baskets.length === 0) return 0;
    let best = baskets[0];
    let bestN = Array.isArray(best?.pieceIds) ? best.pieceIds.length : 0;
    for (let i = 1; i < baskets.length; i += 1) {
        const n = Array.isArray(baskets[i]?.pieceIds) ? baskets[i].pieceIds.length : 0;
        if (n > bestN) {
            best = baskets[i];
            bestN = n;
        }
    }
    return best?.index ?? 0;
}

function resolveShellSx(marketing) {
    if (marketing === "hero") return heroShellSx;
    if (marketing === "giant") return giantShellSx;
    if (marketing === "daily") return dailyShellSx;
    if (marketing === "tools") return toolsShellSx;
    return phoneShellSx;
}

function isFullBleedGiant(marketing) {
    return marketing === "giant";
}

function isFramedMarketing(marketing) {
    return marketing === "hero";
}

function isDarkMarketing(marketing) {
    return (
        marketing === "hero" ||
        marketing === "giant" ||
        marketing === "daily" ||
        marketing === "tools" ||
        marketing === "colorBasket" ||
        marketing === "journey"
    );
}

const CAPTURE_FILL_SX = {
    width: "100%",
    height: "100%",
    minHeight: "100%",
    maxHeight: "100%",
    boxSizing: "border-box",
};

/** Compose at 430×940 on iPhone 6.7" / iPad 13" so title + toolbar stay in frame. */
function JigsawCaptureFrame({ scene, sx, children }) {
    return (
        <ScreenshotDesignFrame background={jigsawShellBgForScene(scene)}>
            <Box sx={{ ...sx, ...CAPTURE_FILL_SX }}>{children}</Box>
        </ScreenshotDesignFrame>
    );
}

/**
 * Google Play screenshot play scene — marketing shells, framed boards, panels, or vault menu.
 */
function ScreenshotGooglePlayPage() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const deviceKey = resolveScreenshotDevice(searchParams.get("device"));
    const pcLandscape = deviceKey === "pc";
    const isSquareAd = searchParams.get("googleAdSquare") === "1";
    const isLandscapeAd = searchParams.get("googleAdLandscape") === "1";
    const facebookAdImageOnly = searchParams.get("facebookAdImageOnly") === "1";
    const facebookAdPortrait45 = searchParams.get("facebookAdPortrait45") === "1";
    const isShowcaseAd = isSquareAd || isLandscapeAd;
    const { t, lang } = useScreenshotTranslation();
    const scene = getScreenshotScene(slug);
    const isVault = isVaultMenuScene(scene);
    const isGallery = isTwoColumnGalleryScene(scene);
    const isJourney = isJourneyMapScene(scene);
    const isColorBasketDemo = isColorBasketDemoScene(scene);
    const marketing = scene?.marketing || null;
    const framed = isFramedMarketing(marketing);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [group, setGroup] = useState(null);
    const [template, setTemplate] = useState(null);
    const [galleryItems, setGalleryItems] = useState([]);
    const [colorBaskets, setColorBaskets] = useState([]);
    const [boardScale, setBoardScale] = useState(1);
    const { mat: boardMat, grooveStyle: boardGrooveStyle } = useBoardMatColor();
    const colorBasketUsesSample = isColorBasketDemo && !scene?.storyId;

    useEffect(() => {
        if (!isShowcaseAd || !scene) return undefined;
        markScreenshotNotReady();
        let cancelled = false;
        (async () => {
            await signalScreenshotSceneReady({
                fontFamily: screenshotFontStack(lang),
                lang,
            });
            if (cancelled) markScreenshotNotReady();
        })();
        return () => {
            cancelled = true;
        };
    }, [isShowcaseAd, scene, slug, lang]);

    useEffect(() => {
        if (isShowcaseAd) {
            setLoading(false);
            return undefined;
        }

        if (isScreenshotSceneExpired(slug)) {
            setLoading(false);
            return undefined;
        }

        if (!scene) {
            setError("Unknown screenshot scene");
            setLoading(false);
            return undefined;
        }

        if (isJourney) {
            setLoading(false);
            setError(null);
            return undefined;
        }

        // Fallback: landing encrypted sample when scene has no storyId.
        if (colorBasketUsesSample) {
            setLoading(false);
            setError(null);
            setColorBaskets([]);
            return undefined;
        }

        if (!isScreenshotSceneConfigured(scene)) {
            setError("Screenshot scene is not configured yet");
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            setGalleryItems([]);
            setGroup(null);
            setTemplate(null);
            setColorBaskets([]);
            try {
                if (isVault || isGallery) {
                    const items = await loadScreenshotGallery(scene);
                    if (cancelled) return;
                    if (!items.length) {
                        setError("No puzzles found for this gallery scene");
                        return;
                    }
                    setGalleryItems(items);
                    setError(null);
                    return;
                }

                const result = await loadScreenshotJigsaw(scene);
                if (cancelled) return;
                setGroup(result.group);
                setTemplate(result.template);
                const grid = getTemplateGrid(result.template);
                if (scene.expectedGrid && grid !== scene.expectedGrid) {
                    setError(
                        `Expected ${scene.expectedGrid}×${scene.expectedGrid} puzzle but loaded ${grid}×${grid}.`,
                    );
                } else {
                    setError(null);
                }

                if (isColorBasketDemo) {
                    // Optional live baskets; layout falls back to 5×5 demo baskets.
                    const baskets = await loadColorBasketsForJigsaw(
                        result.template?.id,
                        scene.preferBasketCount,
                    );
                    if (cancelled) return;
                    setColorBaskets(Array.isArray(baskets) ? baskets : []);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message ?? "Failed to load jigsaw");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        scene,
        slug,
        isVault,
        isGallery,
        isJourney,
        isColorBasketDemo,
        colorBasketUsesSample,
        isShowcaseAd,
    ]);

    const grid = useMemo(() => (template ? getTemplateGrid(template) : 0), [template]);
    const paths = template?.piecePaths?.pieces ?? template?.piecePaths;
    const pieceList = Array.isArray(paths) ? paths : paths?.pieces;
    const imageUrl = getImageUrl(group?.story);
    const title = getStoryTitle(group?.story);
    const total = grid > 0 ? grid * grid : 0;
    const { boardPieceIndices, trayPieceIndices } = useMemo(
        () =>
            computeScreenshotPieceLayout(total, {
                emptyRatio: scene?.emptyRatio,
                emptyRatioMin: scene?.emptyRatioMin,
                emptyRatioMax: scene?.emptyRatioMax,
                emptyZone: scene?.emptyZone ?? "random",
                grid,
                seed: slug ?? "screenshot",
            }),
        [total, slug, scene?.emptyRatio, scene?.emptyRatioMin, scene?.emptyRatioMax, scene?.emptyZone, grid],
    );

    const galleryImageUrls = useMemo(
        () => galleryItems.map((item) => getImageUrl(item?.group?.story)).filter(Boolean),
        [galleryItems],
    );

    useEffect(() => {
        markScreenshotNotReady();
    }, [slug, lang]);

    useEffect(() => {
        if (loading || error) return undefined;

        if (isJourney || colorBasketUsesSample) {
            let cancelled = false;
            (async () => {
                await signalScreenshotSceneReady({
                    fontFamily: screenshotFontStack(lang),
                    lang,
                });
                if (cancelled) markScreenshotNotReady();
            })();
            return () => {
                cancelled = true;
            };
        }

        if (isColorBasketDemo) {
            // Layout uses a marketing 5×5 board + demo baskets; only story art is required.
            if (!imageUrl) {
                return undefined;
            }
            let cancelled = false;
            (async () => {
                await signalScreenshotSceneReady({
                    imageUrl,
                    fontFamily: screenshotFontStack(lang),
                    lang,
                });
                if (cancelled) markScreenshotNotReady();
            })();
            return () => {
                cancelled = true;
            };
        }

        if (isVault || isGallery) {
            if (!galleryItems.length || !galleryImageUrls.length) return undefined;
            let cancelled = false;
            (async () => {
                await signalScreenshotSceneReady({
                    imageUrls: galleryImageUrls,
                    fontFamily: screenshotFontStack(lang),
                    lang,
                });
                if (cancelled) markScreenshotNotReady();
            })();
            return () => {
                cancelled = true;
            };
        }

        if (!template || !pieceList?.length || !imageUrl || grid <= 0) {
            return undefined;
        }
        let cancelled = false;
        (async () => {
            await signalScreenshotSceneReady({
                imageUrl,
                fontFamily: screenshotFontStack(lang),
                lang,
            });
            if (cancelled) markScreenshotNotReady();
        })();
        return () => {
            cancelled = true;
        };
    }, [
        loading,
        error,
        isJourney,
        isColorBasketDemo,
        colorBasketUsesSample,
        colorBaskets,
        isVault,
        isGallery,
        galleryItems,
        galleryImageUrls,
        template,
        pieceList,
        imageUrl,
        grid,
        lang,
        slug,
    ]);

    if (isScreenshotSceneExpired(slug)) {
        return <ScreenshotExpiredPage />;
    }

    if (!scene) {
        return (
            <JigsawCaptureFrame scene={scene} sx={phoneShellSx}>
                <Box sx={{ p: 2 }}>
                    <Typography color="error">{t("mobileScreenshot.common.scene-not-found")}</Typography>
                    <Link component={RouterLink} to={MAIN_PATH} sx={{ color: jigsawScreenshotAccent }}>
                        {t("mobileScreenshot.common.back-to-index")}
                    </Link>
                </Box>
            </JigsawCaptureFrame>
        );
    }

    if (isShowcaseAd) {
        return (
            <Box sx={screenshotShellFontSx(lang)}>
                <JigsawShowcaseGoogleAdCreative
                    scene={scene}
                    lang={lang}
                    variant={isSquareAd ? "square" : "landscape"}
                />
            </Box>
        );
    }

    const headline = t(screenshotSceneKey("jigsaw", slug, "headline"));
    const rawSubtitle = t(screenshotSceneKey("jigsaw", slug, "subtitle"));
    const subtitle =
        rawSubtitle && rawSubtitle !== screenshotSceneKey("jigsaw", slug, "subtitle")
            ? rawSubtitle
            : null;
    const onDark = isDarkMarketing(marketing);

    if (isJourney) {
        return (
            <JigsawCaptureFrame scene={scene} sx={screenshotShellFontSx(lang)}>
                <JourneyMapLayout
                    headline={headline}
                    subtitle={subtitle}
                    compact={facebookAdPortrait45}
                    header={
                        <ScreenshotPromoHeader
                            promoHeadline={headline}
                            promoSubtitle={facebookAdPortrait45 ? null : subtitle}
                            showPills={false}
                            light
                            compact={facebookAdPortrait45}
                        />
                    }
                />
            </JigsawCaptureFrame>
        );
    }

    if (loading) {
        return (
            <JigsawCaptureFrame
                scene={scene}
                sx={{ ...phoneShellSx, alignItems: "center", justifyContent: "center" }}
            >
                <CircularProgress size={32} sx={{ color: jigsawScreenshotAccent }} />
            </JigsawCaptureFrame>
        );
    }

    if (isVault) {
        if (error || !galleryItems.length) {
            return (
                <JigsawCaptureFrame scene={scene} sx={phoneShellSx}>
                    <Box sx={{ p: 2 }}>
                        <Typography color="error" sx={{ mb: 1 }}>
                            {error ?? t("mobileScreenshot.common.could-not-load")}
                        </Typography>
                        <Link component={RouterLink} to={MAIN_PATH} sx={{ color: jigsawScreenshotAccent }}>
                            {t("mobileScreenshot.common.back-to-index")}
                        </Link>
                    </Box>
                </JigsawCaptureFrame>
            );
        }

        return (
            <JigsawCaptureFrame scene={scene} sx={screenshotShellFontSx(lang)}>
                <VaultPackMenu
                    headline={headline}
                    subtitle={facebookAdPortrait45 ? null : subtitle}
                    items={galleryItems}
                    compact={facebookAdPortrait45}
                    landscape={pcLandscape}
                />
            </JigsawCaptureFrame>
        );
    }

    if (isGallery) {
        if (error || !galleryItems.length) {
            return (
                <JigsawCaptureFrame scene={scene} sx={phoneShellSx}>
                    <Box sx={{ p: 2 }}>
                        <Typography color="error" sx={{ mb: 1 }}>
                            {error ?? t("mobileScreenshot.common.could-not-load")}
                        </Typography>
                        <Link component={RouterLink} to={MAIN_PATH} sx={{ color: jigsawScreenshotAccent }}>
                            {t("mobileScreenshot.common.back-to-index")}
                        </Link>
                    </Box>
                </JigsawCaptureFrame>
            );
        }

        return (
            <JigsawCaptureFrame scene={scene} sx={{ ...galleryPhoneShellSx, ...screenshotShellFontSx(lang) }}>
                {facebookAdImageOnly ? null : (
                    <ScreenshotPromoHeader
                        variant="gallery"
                        promoHeadline={headline}
                        showPills={false}
                    />
                )}
                <ScreenshotTwoColumnGallery
                    items={galleryItems}
                    topCount={scene.galleryTopCount ?? 0}
                    columns={pcLandscape ? 5 : 2}
                    getImageUrl={getImageUrl}
                />
            </JigsawCaptureFrame>
        );
    }

    if (colorBasketUsesSample) {
        return (
            <JigsawCaptureFrame scene={scene} sx={screenshotShellFontSx(lang)}>
                <ColorBasketScreenshotLayout headline={headline} subtitle={subtitle} />
            </JigsawCaptureFrame>
        );
    }

    if (error || !template || !pieceList?.length || !imageUrl || grid <= 0) {
        return (
            <JigsawCaptureFrame scene={scene} sx={phoneShellSx}>
                <Box sx={{ p: 2 }}>
                    <Typography color="error" sx={{ mb: 1 }}>
                        {error ?? t("mobileScreenshot.common.could-not-load")}
                    </Typography>
                    <Link component={RouterLink} to={MAIN_PATH} sx={{ color: jigsawScreenshotAccent }}>
                        {t("mobileScreenshot.common.back-to-index")}
                    </Link>
                </Box>
            </JigsawCaptureFrame>
        );
    }

    const boardInner = (
        <ScreenshotBoard
            grid={grid}
            imageUrl={imageUrl}
            paths={pieceList}
            boardPieceIndices={boardPieceIndices}
            svgPathFallback={template.svgPath}
            onScaleChange={setBoardScale}
            matPreset={boardMat}
            matColor={boardMat.hex}
            grooveStyle={boardGrooveStyle}
            fullBleed
        />
    );

    const giantBoard = (
        <ScreenshotBoard
            grid={grid}
            imageUrl={imageUrl}
            paths={pieceList}
            boardPieceIndices={boardPieceIndices}
            svgPathFallback={template.svgPath}
            onScaleChange={setBoardScale}
            matPreset={boardMat}
            matColor={boardMat.hex}
            grooveStyle={boardGrooveStyle}
            fullBleed
            fillHeight
            coverViewport
            showFullImage
            fullImageFit="cover"
            showDieLines
        />
    );

    if (marketing === "sorting") {
        return (
            <JigsawCaptureFrame scene={scene} sx={screenshotShellFontSx(lang)}>
                <SortingAsymLayout
                    headline={headline}
                    board={boardInner}
                    pieceIndices={trayPieceIndices}
                    imageUrl={imageUrl}
                    grid={grid}
                    paths={pieceList}
                    compact={facebookAdPortrait45}
                    landscape={pcLandscape}
                />
            </JigsawCaptureFrame>
        );
    }

    if (marketing === "colorBasket") {
        return (
            <JigsawCaptureFrame scene={scene} sx={screenshotShellFontSx(lang)}>
                <ColorBasketScreenshotLayout
                    headline={headline}
                    subtitle={subtitle}
                    baskets={colorBaskets}
                    imageUrl={imageUrl}
                    grid={grid}
                    paths={pieceList}
                    matPreset={boardMat}
                    matColor={boardMat.hex}
                    grooveStyle={boardGrooveStyle}
                    highlightBasketIndex={pickHighlightBasketIndex(colorBaskets)}
                    landscape={pcLandscape}
                />
            </JigsawCaptureFrame>
        );
    }

    if (isFullBleedGiant(marketing)) {
        return (
            <JigsawCaptureFrame
                scene={scene}
                sx={{
                    ...giantShellSx,
                    ...screenshotShellFontSx(lang),
                    position: "relative",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 1,
                    }}
                >
                    {giantBoard}
                </Box>
                <ScreenshotPiecesDifficultyBar
                    piecesLabel={scene.piecesLabel}
                    overlay
                    singlePiecesPill
                    headline={facebookAdImageOnly ? null : headline}
                />
            </JigsawCaptureFrame>
        );
    }

    const shellSx = resolveShellSx(marketing);
    const hideStoryMeta = Boolean(scene.hideStoryMeta);
    const hideDefaultTray = marketing === "hero" || marketing === "daily" || marketing === "tools";

    const boardWithOptionalGlow =
        marketing === "daily" ? (
            <Box sx={glowingBoardWrapSx}>{boardInner}</Box>
        ) : (
            boardInner
        );

    return (
        <JigsawCaptureFrame
            scene={scene}
            sx={{
                ...shellSx,
                ...screenshotShellFontSx(lang),
                position: "relative",
                ...(pcLandscape
                    ? {
                          pt: "24px",
                          pb: "20px",
                          px: 3,
                      }
                    : facebookAdPortrait45
                    ? {
                          pt: "max(env(safe-area-inset-top, 0px), 10px)",
                          pb: "max(env(safe-area-inset-bottom, 0px), 4px)",
                      }
                    : {}),
            }}
        >
            {marketing === "daily" ? <DailyParticleField /> : null}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: pcLandscape ? "row" : "column",
                    alignItems: "stretch",
                    gap: pcLandscape ? 3 : 0,
                    flex: 1,
                    minHeight: 0,
                    ...(facebookAdPortrait45 ? { gap: 0, justifyContent: "flex-start" } : {}),
                }}
            >
                <Box
                    sx={
                        pcLandscape
                            ? {
                                  flex: "0 1 42%",
                                  minWidth: 0,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                              }
                            : { display: "contents" }
                    }
                >
                <ScreenshotPromoHeader
                    promoHeadline={headline}
                    promoSubtitle={facebookAdPortrait45 ? null : subtitle}
                    showPills={Boolean(scene.hidePills === false) && !facebookAdPortrait45}
                    light={onDark}
                    compact={facebookAdPortrait45 || pcLandscape}
                />
                {pcLandscape ? (
                    <>
                        {!hideStoryMeta && !facebookAdPortrait45 ? (
                            <ScreenshotPuzzleTitleBar title={title} template={template} grid={grid} />
                        ) : null}
                        {marketing === "hero" ? (
                            <ScreenshotPiecesDifficultyBar
                                piecesLabel={scene.piecesLabel}
                                difficultyLabel={scene.difficultyLabel}
                                compact
                            />
                        ) : null}
                        {marketing === "daily" ? <DailyLiveOpsPanel compact /> : null}
                        {marketing === "tools" ? <ToolsUtilityBar /> : null}
                        {!hideDefaultTray ? (
                            <Box sx={trayRegionSx}>
                                <ScreenshotPiecesTray
                                    pieceIndices={trayPieceIndices}
                                    imageUrl={imageUrl}
                                    grid={grid}
                                    paths={pieceList}
                                    boardScale={boardScale}
                                />
                            </Box>
                        ) : null}
                    </>
                ) : null}
                </Box>

                <Box
                    sx={{
                        ...(framed ? boardRegionFramedSx : boardRegionSx),
                        ...(pcLandscape
                            ? {
                                  flex: "1 1 auto",
                                  minWidth: 0,
                                  height: "100%",
                                  width: "auto",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  px: 0,
                                  py: 0,
                              }
                            : {}),
                        ...(facebookAdPortrait45
                            ? {
                                  flex: "0 0 auto",
                                  width: "100%",
                                  alignSelf: "stretch",
                                  py: 0,
                                  px: 0,
                                  mt: 0,
                                  mb: 0,
                              }
                            : {}),
                    }}
                >
                    {framed && !pcLandscape ? (
                        <ScreenshotPhoneFrame>
                            <Box sx={boardFrameSx}>{boardWithOptionalGlow}</Box>
                        </ScreenshotPhoneFrame>
                    ) : marketing === "daily" || marketing === "tools" || (framed && pcLandscape) ? (
                        <Box
                            sx={{
                                px: pcLandscape || facebookAdPortrait45 ? 0 : 0.75,
                                width: pcLandscape
                                    ? "auto"
                                    : facebookAdPortrait45 && marketing === "daily"
                                      ? "72%"
                                      : "100%",
                                height: pcLandscape ? "100%" : undefined,
                                maxWidth: pcLandscape ? "100%" : undefined,
                                aspectRatio: pcLandscape ? "1" : undefined,
                                mx: !pcLandscape && facebookAdPortrait45 && marketing === "daily" ? "auto" : undefined,
                            }}
                        >
                            <Box
                                sx={{
                                    ...boardFrameSx,
                                    borderRadius: facebookAdPortrait45 ? 1.5 : 2,
                                    overflow: "hidden",
                                    ...(pcLandscape
                                        ? {
                                              height: "100%",
                                              width: "auto",
                                              maxWidth: "100%",
                                              aspectRatio: "1",
                                          }
                                        : {}),
                                }}
                            >
                                {boardWithOptionalGlow}
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ ...boardFrameSx, width: "100%" }}>{boardWithOptionalGlow}</Box>
                    )}
                </Box>

                {!pcLandscape && !hideStoryMeta && !facebookAdPortrait45 ? (
                    <ScreenshotPuzzleTitleBar title={title} template={template} grid={grid} />
                ) : null}

                {!pcLandscape && marketing === "hero" ? (
                    <ScreenshotPiecesDifficultyBar
                        piecesLabel={scene.piecesLabel}
                        difficultyLabel={scene.difficultyLabel}
                        compact={facebookAdPortrait45}
                    />
                ) : null}

                {!pcLandscape && marketing === "daily" ? (
                    <DailyLiveOpsPanel compact={facebookAdPortrait45} />
                ) : null}
                {!pcLandscape && marketing === "tools" && !facebookAdPortrait45 ? <ToolsUtilityBar /> : null}

                {!pcLandscape && !hideDefaultTray ? (
                    <Box sx={trayRegionSx}>
                        <ScreenshotPiecesTray
                            pieceIndices={trayPieceIndices}
                            imageUrl={imageUrl}
                            grid={grid}
                            paths={pieceList}
                            boardScale={boardScale}
                        />
                    </Box>
                ) : null}
            </Box>
        </JigsawCaptureFrame>
    );
}

/**
 * Capture router: Apple iPhone 6.7" / iPad 13" use in-app play UI.
 * Google Play phone and tablet keep the marketing layouts.
 * Google Play PC (`device=pc`) uses the same scenes in a 1920×1080 landscape frame.
 */
export default function ScreenshotPlayPage() {
    const [searchParams] = useSearchParams();
    const device = resolveScreenshotDevice(searchParams.get("device"));
    if (SCREENSHOT_DEVICES[device]?.group === "apple") {
        return <ScreenshotApplePlayPage />;
    }
    return <ScreenshotGooglePlayPage />;
}
